import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect } from 'react';
import { formatLoyaltyPointReason, formatPaymentType, PaymentType } from '../../common/enums';
import { formatDate } from '../../common/timeFunctions';
import { isStaff } from '../../common/typeAssertionFunctions';
import { CustomerInfo } from '../../common/types';
import useAuthStore from '../../datastore/useAuth';
import useLoyaltyPoints from '../../datastore/useLoyaltyPoints';

interface Props {
    customer: CustomerInfo
    onClose: () => void
}

const formatLoyaltyPointChange = (change: number): string => {
    return [
        change === 0 ? '' : change > 0 ? '+' : '-',
        Math.abs(change)
    ].join('')
}

export default function CustomerLoyaltyPointsHistoryModal({ customer, onClose }: Props) {
    const { currentUser } = useAuthStore()
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { loyaltyPoints, loading, fetchLoyaltyPointsForCustomer } = useLoyaltyPoints();

    const linkPrefix = isStaff(currentUser) ? '/staff/pos' : '/orderConfirmed'
    const nameLabel = isStaff(currentUser) ? `${[customer.firstName, customer.lastName].join(' ').trim()}'s` : 'My'

    useEffect(() => {
        if (customer.id && fetchLoyaltyPointsForCustomer) {
            fetchLoyaltyPointsForCustomer(customer.id)
        }
    }, [customer.id, fetchLoyaltyPointsForCustomer])


    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{ height: '80%' }}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="h3" fontWeight="bold">
                        {nameLabel} {formatPaymentType(PaymentType.LoyaltyPoints)}
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <LinearProgress variant='indeterminate' color='primary' />
                ) : loyaltyPoints.length === 0 ? (
                    <Typography variant="h5">No {formatPaymentType(PaymentType.LoyaltyPoints)} Found</Typography>
                ) : isMobile ? (
                    // Mobile-friendly stacked layout
                    <Stack spacing={1} direction="column">
                        {loyaltyPoints.map(lp => (
                            <Card sx={{ minWidth: 275 }}>
                                <CardContent sx={{ paddingBottom: 0 }}>
                                    <Typography gutterBottom sx={{ color: 'text.secondary', fontSize: 14 }}>
                                        <strong> {formatDate(new Date(lp.createdAt))}</strong>
                                    </Typography>
                                    <Typography><strong>Opening Balance:</strong> {lp.openingBalance}</Typography>
                                    <Typography>
                                        <strong>Change:</strong>{' '}
                                        <span style={{ color: lp.change >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
                                            {formatLoyaltyPointChange(lp.change)}
                                        </span>
                                    </Typography>
                                    <Typography><strong>Type:</strong> {formatLoyaltyPointReason(lp.reason)}</Typography>
                                    <Typography><strong>Closing Balance:</strong> {lp.closingBalance}</Typography>
                                </CardContent>
                                <CardActions sx={{ paddingY: 0 }}>
                                    <Button role="link" href={`${linkPrefix}/${lp.orderId}`}>{`#${lp.orderId}`}</Button>
                                </CardActions>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    // Desktop Table Layout
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Opening Balance</TableCell>
                                    <TableCell>Change</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Closing Balance</TableCell>
                                    <TableCell>Order</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loyaltyPoints.map((loyaltyPoint) => (
                                    <TableRow key={loyaltyPoint.id}>
                                        <TableCell>{formatDate(new Date(loyaltyPoint.createdAt))}</TableCell>
                                        <TableCell>{loyaltyPoint.openingBalance}</TableCell>
                                        <TableCell>
                                            <Typography sx={{ color: loyaltyPoint.change >= 0 ? theme.palette.success.main : theme.palette.error.main }}>
                                                {formatLoyaltyPointChange(loyaltyPoint.change)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{formatLoyaltyPointReason(loyaltyPoint.reason)}</TableCell>
                                        <TableCell>{loyaltyPoint.closingBalance}</TableCell>
                                        <TableCell>
                                            <Button role="link" href={`${linkPrefix}/${loyaltyPoint.orderId}`}>{`#${loyaltyPoint.orderId}`}</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    );
}